import { useEffect, useRef, useState } from "react";
import { buildPreview, PREVIEW_SANDBOX, type BridgeMessage } from "@buildlab/preview";
import type { FileMap } from "@buildlab/shared";
import { clockTime } from "@buildlab/shared/golf";
import { AgentPane } from "./AgentPane";
import { answerDataRequest, api, golfApi, type GolfAttempt, type GolfCheckResult, type GolfHoleInfo } from "./api";
import { BigExit, FitControls, useFit } from "./FitFrame";
import { GolfBoardDialog } from "./GolfBoard";
import { Back, Check, Restart } from "./icons";
import { Lion } from "./Lion";
import { useMe } from "./me";
import { Preview } from "./Preview";

// Prompt Golf, playing one hole (Review 19, D53). Three columns: the finished app (play it, it's
// read-only), the student's own rebuild (an empty project only the helper changes: no Code or
// Cards, no hand editing) and the helper in Build mode. Every message is a stroke. "Check my build"
// runs the hole's hidden checks one at a time in a fresh copy of the rebuild, inside the same
// sandbox as the preview, and shows how many passed and what's still missing, in plain words.

const CHECK_TIMEOUT_MS = 15_000;

/** Runs one check script in a fresh, sandboxed copy of the build, inside `host`. Resolves pass/fail. */
function runCheck(host: HTMLElement, html: string, script: string): Promise<boolean> {
  return new Promise((resolve) => {
    const frame = document.createElement("iframe");
    frame.setAttribute("sandbox", PREVIEW_SANDBOX);
    frame.setAttribute("title", "Your build, being checked");
    frame.srcdoc = html;
    let started = false;
    let done = false;
    const finish = (pass: boolean) => {
      if (done) return;
      done = true;
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
      frame.remove();
      resolve(pass);
    };
    const onMessage = (e: MessageEvent) => {
      if (e.source !== frame.contentWindow) return;
      const m = e.data as BridgeMessage;
      if (!m || m.__buildlab !== 1) return;
      if (m.kind === "getData") answerDataRequest(frame.contentWindow!, m);
      else if (m.kind === "ready" && !started) {
        started = true;
        setTimeout(() => frame.contentWindow?.postMessage({ __buildlabTest: 1, id: 1, script }, "*"), 500);
      } else if (m.kind === "test") finish(m.pass);
    };
    window.addEventListener("message", onMessage);
    const timer = setTimeout(() => finish(false), CHECK_TIMEOUT_MS);
    host.appendChild(frame);
  });
}

type Checking = { state: "idle" } | { state: "running"; at: number; of: number } | { state: "done"; result: GolfCheckResult } | { state: "error"; message: string };

export function GolfPlay({ holeId, onExit }: { holeId: string; onExit: () => void }) {
  const me = useMe();
  const [hole, setHole] = useState<GolfHoleInfo | null>(null);
  const [attempt, setAttempt] = useState<GolfAttempt | null>(null);
  const [files, setFiles] = useState<FileMap | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [round, setRound] = useState(0); // Try again starts a fresh round
  const [helperBusy, setHelperBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [attachErrors, setAttachErrors] = useState(false);
  const [checking, setChecking] = useState<Checking>({ state: "idle" });
  const [card, setCard] = useState<GolfCheckResult | null>(null);
  const [boardOpen, setBoardOpen] = useState(false);
  const [targetRun, setTargetRun] = useState(0);
  const checkHost = useRef<HTMLDivElement>(null);
  const targetFrame = useRef<HTMLIFrameElement>(null);
  const fit = useFit(targetFrame, `${hole?.id}:${targetRun}`);

  // The hole, then this round: the last unfinished one on first open, a fresh one after Try again.
  useEffect(() => {
    let cancelled = false;
    setFiles(null);
    setChecking({ state: "idle" });
    (async () => {
      const [{ hole }, { attempt }] = await Promise.all([golfApi.hole(holeId), golfApi.start(holeId, round === 0)]);
      const { project } = await api.project(attempt.projectId);
      if (cancelled) return;
      setHole(hole);
      setAttempt(attempt);
      setFiles(project.files);
    })().catch((e: Error) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [holeId, round]);

  // Esc closes the board or the score card; the page itself stays.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setBoardOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const refreshAttempt = () => {
    if (attempt) void golfApi.attempt(attempt.id).then((r) => setAttempt(r.attempt), () => {});
  };

  async function checkBuild() {
    if (!attempt || !checkHost.current) return;
    setChecking({ state: "running", at: 0, of: hole?.checks ?? 0 });
    try {
      const [{ checks }, { project }] = await Promise.all([golfApi.checks(attempt.id), api.project(attempt.projectId)]);
      const html = buildPreview(project.files).html;
      const results: Record<string, boolean> = {};
      for (let i = 0; i < checks.length; i++) {
        setChecking({ state: "running", at: i + 1, of: checks.length });
        results[checks[i]!.id] = await runCheck(checkHost.current, html, checks[i]!.script);
      }
      const result = await golfApi.report(attempt.id, results);
      setAttempt(result.attempt);
      if (result.finished) {
        setChecking({ state: "idle" });
        setCard(result);
      } else setChecking({ state: "done", result });
    } catch (e) {
      setChecking({ state: "error", message: e instanceof Error ? e.message : "The check couldn't run." });
    }
  }

  const tryAgain = () => {
    setCard(null);
    setBoardOpen(false);
    setRound((r) => r + 1);
  };

  if (error) {
    return (
      <div className="golf-play golf-message">
        <Lion pose="think" size={104} label="Roary, thinking" />
        <p className="error-text">{error}</p>
        <button type="button" className="outline" onClick={onExit}>
          Back to Home
        </button>
      </div>
    );
  }

  const finished = !!attempt?.finishedAt;
  const running = checking.state === "running";
  return (
    <div className="golf-play" role="main" aria-label="Prompt Golf">
      <header className="topbar golf-bar">
        <button type="button" className="pill-dark back" title="Back to Home" onClick={onExit}>
          <Back />
          <span className="bar-label">Home</span>
        </button>
        <div className="title">
          <span className="golf-flag" aria-hidden="true">
            ⛳
          </span>
          <span className="eyebrow gold">Prompt Golf</span>
          <h1>{hole ? `Hole ${hole.number} · ${hole.title}` : "Loading the hole…"}</h1>
        </div>
        {hole && attempt && (
          <div className="golf-meter" aria-label="This round">
            <span className="golf-chip">
              <span className="k">Par</span> <b>{hole.par}</b>
            </span>
            <span className={`golf-chip strokes${attempt.strokes > hole.par ? " over" : ""}`} title="A stroke is one message to the helper">
              <span className="k">Strokes</span> <b data-testid="golf-strokes">{attempt.strokes}</b>
            </span>
            <span className="golf-chip" title="Words in your messages (fewer wins a tie)">
              <span className="k">Words</span> <b data-testid="golf-words">{attempt.words}</b>
            </span>
          </div>
        )}
        <button type="button" className="pill-dark" onClick={() => setBoardOpen(true)} disabled={!hole}>
          <span aria-hidden="true">🏆</span>
          <span className="bar-label">Board</span>
        </button>
        <button type="button" className="primary golf-check-btn" onClick={() => void checkBuild()} disabled={!attempt || running || helperBusy || finished}>
          {running ? "Checking…" : "Check my build"}
        </button>
      </header>

      <div className="golf-cols">
        <section className="preview golf-target" aria-label="The app to rebuild">
          <div className="pane-head">
            <h2 className="eyebrow">Rebuild this</h2>
            <FitControls fit={fit} />
            <button type="button" className="ghost" onClick={() => setTargetRun((r) => r + 1)} title="Start it again from the beginning">
              <Restart size={14} />
              <span className="bar-label">Restart</span>
            </button>
          </div>
          {hole && <p className="golf-brief">{hole.brief}</p>}
          <div ref={fit.stageRef} className={`stage ${fit.stageClass}`}>
            {hole && <iframe key={targetRun} ref={targetFrame} title={`${hole.title}, the finished app`} sandbox={PREVIEW_SANDBOX} srcDoc={buildPreview(hole.files).html} style={fit.frameStyle} />}
            <BigExit fit={fit} />
          </div>
        </section>

        <div className="golf-build">
          {attempt && files ? (
            <Preview
              key={attempt.projectId}
              projectId={attempt.projectId}
              files={files}
              caption={helperBusy ? "The helper is building…" : "Your rebuild"}
              compact
              errorDetails
              onErrorsChange={setErrors}
            />
          ) : (
            <section className="preview" aria-label="Your rebuild">
              <p className="golf-wait muted">Setting up your round…</p>
            </section>
          )}
          {/* Check my build: each check plays a fresh copy of the build here, on top of the preview. */}
          <div className={`golf-checking${running ? " on" : ""}`} aria-hidden={!running}>
            <p className="golf-checking-head">
              Checking your build… {checking.state === "running" && checking.at > 0 ? `${checking.at} of ${checking.of}` : ""}
            </p>
            <div ref={checkHost} className="golf-checking-stage" />
          </div>
          {checking.state === "done" && (
            <div className="golf-result" role="status">
              <div className="row between">
                <b className="golf-result-count">
                  {checking.result.attempt.passed} of {checking.result.attempt.total} checks pass
                </b>
                <button type="button" className="icon-close" aria-label="Close" onClick={() => setChecking({ state: "idle" })}>
                  ×
                </button>
              </div>
              <div className="golf-pips" aria-hidden="true">
                {Array.from({ length: checking.result.attempt.total }, (_, i) => (
                  <span key={i} className={i < checking.result.attempt.passed ? "on" : ""} />
                ))}
              </div>
              {checking.result.note ? (
                <p className="small">{checking.result.note}</p>
              ) : (
                <>
                  <p className="small muted">{checking.result.attempt.total - checking.result.attempt.passed > 1 ? "One thing still missing:" : "Still missing:"}</p>
                  <ul className="golf-missing">
                    {checking.result.missing.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                  <p className="small muted">Tell the helper exactly what's missing. Every message is a stroke.</p>
                </>
              )}
            </div>
          )}
          {checking.state === "error" && (
            <p className="golf-result page-error" role="alert">
              {checking.message}
            </p>
          )}
        </div>

        <div className="golf-helper">
          {attempt && !finished ? (
            <AgentPane
              key={attempt.id}
              golf
              projectId={attempt.projectId}
              assignment={null}
              allowedModes={["build"]}
              errors={errors}
              attachErrors={attachErrors}
              onAttachErrors={setAttachErrors}
              onFiles={(f) => setFiles(f)}
              onTurnStart={async () => {}}
              onRunningChange={(busy) => {
                setHelperBusy(busy);
                if (busy) setChecking((c) => (c.state === "running" ? c : { state: "idle" }));
                else refreshAttempt();
              }}
              onFileChange={(path, content) =>
                setFiles((f) => {
                  if (!f) return f;
                  const next = { ...f };
                  if (content === null) delete next[path];
                  else next[path] = content;
                  return next;
                })
              }
              picked={null}
              onClearPicked={() => {}}
            />
          ) : (
            <section className="agent golf-over" aria-label="Helper">
              {attempt?.finishedAt && attempt.score ? (
                <>
                  <Lion pose="cheer" size={78} label="Roary, cheering" />
                  <p className="golf-over-label">{attempt.score.label}</p>
                  <p>
                    {attempt.strokes} {attempt.strokes === 1 ? "stroke" : "strokes"} · {attempt.words} words
                  </p>
                  <button type="button" className="primary" onClick={tryAgain}>
                    Try again
                  </button>
                </>
              ) : (
                <p className="muted">Getting the helper ready…</p>
              )}
            </section>
          )}
        </div>
      </div>

      {card && hole && <ScoreCard result={card} holeTitle={hole.title} holeNumber={hole.number} onAgain={tryAgain} onBoard={() => setBoardOpen(true)} onClose={() => setCard(null)} onHome={onExit} />}
      {boardOpen && hole && <GolfBoardDialog holeId={hole.id} title={`Hole ${hole.number} · ${hole.title}`} par={hole.par} teacher={me?.user.role === "teacher"} onClose={() => setBoardOpen(false)} />}
    </div>
  );
}

/** The arcade score card when every check passes. */
function ScoreCard({
  result,
  holeTitle,
  holeNumber,
  onAgain,
  onBoard,
  onClose,
  onHome,
}: {
  result: GolfCheckResult;
  holeTitle: string;
  holeNumber: number;
  onAgain: () => void;
  onBoard: () => void;
  onClose: () => void;
  onHome: () => void;
}) {
  const a = result.attempt;
  const score = a.score!;
  const vsPar = score.diff === 0 ? "even with par" : score.diff < 0 ? `${-score.diff} under par` : `${score.diff} over par`;
  return (
    <div className="golf-card-backdrop" role="dialog" aria-modal="true" aria-label="Your score">
      <div className={`golf-scorecard k-${score.kind}`}>
        <p className="eyebrow gold">
          Hole {holeNumber} · {holeTitle} · Rebuilt!
        </p>
        <p className="golf-big">{score.label}</p>
        <Lion pose="cheer" size={96} bob label="Roary, cheering" />
        <dl className="golf-stats">
          <div>
            <dt>Strokes</dt>
            <dd>{a.strokes}</dd>
          </div>
          <div>
            <dt>Par</dt>
            <dd>{a.par}</dd>
          </div>
          <div>
            <dt>Words</dt>
            <dd>{a.words}</dd>
          </div>
          <div>
            <dt>Time</dt>
            <dd>{clockTime(a.seconds ?? 0)}</dd>
          </div>
        </dl>
        <p className="golf-sub">
          {a.strokes} {a.strokes === 1 ? "message" : "messages"}, {vsPar}.{result.newBest ? " " : ""}
          {result.newBest && (
            <span className="golf-newbest">
              <Check size={12} width={3} /> New best!
            </span>
          )}
        </p>
        <div className="row golf-card-actions">
          <button type="button" className="primary" onClick={onAgain} autoFocus>
            Try again
          </button>
          <button type="button" className="outline" onClick={onBoard}>
            See the board
          </button>
          <button type="button" className="outline" onClick={onClose}>
            Look at my build
          </button>
          <button type="button" className="linklike" onClick={onHome}>
            Home
          </button>
        </div>
      </div>
    </div>
  );
}
